import React, { useEffect, useState } from 'react';
import {
    Box,
    colors,
    MenuItem,
    Typography
} from '@mui/material';
import IconButtonBox from '../../components/IconButtonBox';
import ButtonBox from '../../components/ButtonBox';
import DialogBox from '../../components/DialogBox';
import TextInputBox from '../../components/TextInputBox';
import AddIcon from '@mui/icons-material/Add';
import CachedIcon from '@mui/icons-material/Cached';
import MenuBox from '../../components/MenuBox';
import NewTableBox from '../../components/NewTableBox';
import MemberApi from '../../api/MemberApi';
import { useError } from '../../utils/functions';
import usePaginationStore from '../../stores/PaginationStore';
import useTableStore from '../../stores/TableStore';

const Member: React.FC = () => {
    const { setError } = useError();
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);
    const [open, setOpen] = useState(false);
    const [delOpen, setDelOpen] = useState(false);
    const [openType, setOpenType] = useState("");
    const [data, setData] = useState([]);
    const [formValues, setFormValues] = useState({
        name: '',
        email: '',
        id: '',
        password: '',
    });

    const [totalCount, setTotalCount] = useState(0);
    const { setBeforeCursor, setAfterCursor, setReQuery, pageAction, closeAction, resetStore } = usePaginationStore();
    const { setContinuousMenu } = useTableStore();

    const columns = [
        { field: "name", title: "Name", isSearch: true },
        { field: "email", title: "Email", isSearch: true },
        { field: "lastLoginedAt", title: "Last Logged-in At" },
        { field: "createdAt", title: "Created At", isSort: true },
    ];

    const handleAdd = async () => {
        let isError = false;
        if (openType === 'add') {
            const memberResponse = await MemberApi.createMember(formValues.name, formValues.email, formValues.password);
            if (memberResponse.code) {
                setError(memberResponse.message);
                isError = true;
            }
        } else if (openType === 'edit') {
            const memberResponse = await MemberApi.updateMember(formValues.id, formValues.name, formValues.email, '');
            if (memberResponse.code) {
                setError(memberResponse.message);
                isError = true;
            }
        } else if (openType === 'change') {
            const memberResponse = await MemberApi.updateMember(formValues.id, formValues.name, formValues.email, formValues.password);
            if (memberResponse.code) {
                setError(memberResponse.message);
                isError = true;
            }
        }

        if (!isError) {
            setReQuery(true);
            resetStore();
            queryMember();
            handleClose();
        }
    };

    const setValue = (field: string, value: string) => {
        setFormValues((prevValues) => ({ ...prevValues, [field]: value }));
    };

    const handleData = (type: string, row: any) => {
        clearFormValues();
        setOpenType(type);

        if (row) {
            setValue('email', row.email);
            setValue('name', row.name);
            setValue('id', row.id);
        }

        if (type === 'delete') {
            setDelOpen(true);
        } else {
            handleOpen();
        }
    }

    const clearFormValues = () => {
        setFormValues({
            name: '',
            email: '',
            id: '',
            password: '',
        });
    }

    const queryMember = async () => {
        setData([]);
        try {
            const { limit, beforeCursor, afterCursor } = usePaginationStore.getState();

            const paginationParams = {
                limit: limit,
                beforeCursor: beforeCursor,
                afterCursor: afterCursor,
            };

            const memberResponse = await MemberApi.getMember(paginationParams);

            if (memberResponse.code) {
                setError(memberResponse.message);
            } else {
                setData(memberResponse.data);

                if (memberResponse.metadata && memberResponse.metadata.total) {
                    setTotalCount(memberResponse.metadata.total);
                }

                if (memberResponse.paging) {
                    setAfterCursor(memberResponse.paging.afterCursor);
                    setBeforeCursor(memberResponse.paging.beforeCursor);
                }

            }
        } catch (error) {
            console.error("Error fetching memberResponse data:", error);
        } finally {
            closeAction();
        }
    }

    const handleDelete = async () => {
        if (formValues.id) {
            const res = await MemberApi.deleteMember(formValues.id);

            if (res.code) {
                setError(res.message);
            } else {
                setDelOpen(false);
                setValue('id', '');
                setValue('name', '');
                setReQuery(true);
                resetStore();
                queryMember();
            }
        }
    };

    useEffect(() => {
        setContinuousMenu(false);
        resetStore();
        queryMember();
    }, []);

    useEffect(() => {
        if (pageAction) {
            queryMember();
        }
    }, [pageAction]);

    return (
        <>
            <Typography className="layout-title">
                Member
            </Typography>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box />
                <Box>
                    <IconButtonBox
                        icon={<CachedIcon />}
                        text='Refresh'
                        onClick={queryMember} />
                    <ButtonBox
                        text='Create new'
                        onClick={() => { handleData('add', null) }}
                        icon={<AddIcon />} />
                </Box>
            </Box>
            <NewTableBox
                columns={columns}
                data={data}
                showDelete={false}
                renderMoreContent={(row) => (
                    <MenuBox>
                        <MenuItem onClick={() => { handleData('edit', row) }}>
                            Edit name
                        </MenuItem>
                        <MenuItem onClick={() => handleData('change', row)}>
                            Change password
                        </MenuItem>
                        <MenuItem onClick={() => handleData('delete', row)}>
                            <span style={{ color: "red" }}>Delete</span>
                        </MenuItem>
                    </MenuBox >
                )}
                totalCount={totalCount}
            />
            < DialogBox
                open={open}
                title={
                    openType === 'add'
                        ? "Add a member"
                        : openType === 'edit'
                            ? "Edit name"
                            : openType === 'change'
                                ? "Change Password"
                                : ""
                }
                dialogContent={
                    openType === 'add' ?
                        <>
                            <TextInputBox
                                label="Name"
                                placeholder='Name'
                                value={formValues.name}
                                onChange={(e) => setValue('name', e.target.value)}
                            />
                            <TextInputBox
                                label="Email"
                                placeholder='Email'
                                value={formValues.email}
                                onChange={(e) => setValue('email', e.target.value)}
                            />
                            <TextInputBox
                                label="Password"
                                placeholder='Password'
                                value={formValues.password}
                                onChange={(e) => setValue('password', e.target.value)}
                                type="password"
                            />
                        </>
                        : openType === 'edit' ?
                            <>
                                <TextInputBox
                                    label="Email"
                                    placeholder='Email'
                                    disabled={true}
                                    value={formValues.email}
                                    onChange={(e) => setValue('email', e.target.value)}
                                />
                                <TextInputBox
                                    label="Name"
                                    placeholder='Name'
                                    value={formValues.name}
                                    onChange={(e) => setValue('name', e.target.value)}
                                />
                            </>
                            : openType === 'change' ?
                                <>
                                    <TextInputBox
                                        label="New password"
                                        placeholder='Password'
                                        value={formValues.password}
                                        onChange={(e) => setValue('password', e.target.value)}
                                        type="password"
                                    />
                                </>
                                : ""
                }
                onCancel={handleClose}
                onConfirm={handleAdd}
                confirmLabel={openType === "add" ? "Add" : "Save"}
            />
            <DialogBox
                open={delOpen}
                title='Delete'
                dialogContent={
                    <>
                        {formValues.name} will be permanently deleted.
                    </>
                }
                showCancel={true}
                onCancel={() => setDelOpen(false)}
                onConfirm={handleDelete}
                isError={true}
                confirmLabel='Delete'
            />
        </>
    );
};

export default Member;
